import manifest from '../shared/hd-runtime.json';
import aliasManifest from '../shared/asset-aliases.json';
import { packedAssetUrl } from './asset-delivery';

type Texture = readonly [path: string, logicalWidth: number, logicalHeight: number, scale: number];
type Alias = readonly [source: string, canonical: string, logicalWidth: number, logicalHeight: number, scale: number, owner: string];
const textures = manifest as unknown as Record<string, Texture>;
const aliases = aliasManifest.entries as unknown as Record<string, Alias>;
const imageTextures = new WeakMap<object, Texture>();

function sharedTexture(path: string): Alias | undefined {
  const alias = aliases[path];
  if (!alias) return;
  const texture = textures[path], owner = textures[alias[5]];
  // A subsequent HD merge can retire either side; never reuse old geometry.
  if ((texture?.[0] ?? path) !== alias[0] || (owner?.[0] ?? alias[5]) !== alias[1]) return;
  for (const value of [texture, owner]) {
    if (value ? value[1] !== alias[2] || value[2] !== alias[3] || value[3] !== alias[4] : alias[4] !== 1) return;
  }
  return alias;
}

export const assetSourceUrl = (path: string): string => sharedTexture(path)?.[1] ?? textures[path]?.[0] ?? path;
export const assetUrl = (path: string): string => packedAssetUrl(assetSourceUrl(path)) ?? assetSourceUrl(path);

/** Geometry belongs to the cache identity as well as the canonical file. */
export function assetCacheKey(path: string): string {
  const texture = textures[path], alias = sharedTexture(path);
  return JSON.stringify(texture ? [assetSourceUrl(path), texture[1], texture[2], texture[3]] : alias
    ? [assetSourceUrl(path), alias[2], alias[3], alias[4]] : [assetSourceUrl(path), 'native']);
}

export function bindAssetImage(image: HTMLImageElement, path: string): void {
  const texture = textures[path];
  if (texture) imageTextures.set(image, texture);
  image.src = assetUrl(path);
}
export function logicalImageSize(image: HTMLImageElement): { width: number; height: number } {
  const texture = imageTextures.get(image);
  return { width: texture?.[1] ?? image.naturalWidth ?? image.width, height: texture?.[2] ?? image.naturalHeight ?? image.height };
}

/** Source pivots and collision geometry use Flash pixels, never texture pixels. */
export function drawLogicalImage(ctx: CanvasRenderingContext2D, image: CanvasImageSource, ...coordinates: number[]): void {
  const texture = imageTextures.get(image);
  if (coordinates.length === 2) {
    if (texture) ctx.drawImage(image, coordinates[0], coordinates[1], texture[1], texture[2]);
    else ctx.drawImage(image, coordinates[0], coordinates[1]);
  } else if (coordinates.length === 4) {
    ctx.drawImage(image, coordinates[0], coordinates[1], coordinates[2], coordinates[3]);
  } else if (coordinates.length === 8) {
    const scale = texture?.[3] ?? 1;
    ctx.drawImage(image, coordinates[0] * scale, coordinates[1] * scale, coordinates[2] * scale, coordinates[3] * scale,
      coordinates[4], coordinates[5], coordinates[6], coordinates[7]);
  } else throw new Error('Invalid texture draw coordinates');
}
